import { Field, connect, getIn } from 'formik';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';

import Error from 'hb-react/shared/components/formik/components/Error';
import Label from 'hb-react/shared/components/formik/components/Label';
import Wrapper from 'hb-react/shared/components/formik/components/Wrapper';
import { addClassName } from 'hb-react/shared/components/formik/utils/updateClassName';

import { validatePassword } from './validate';

const Password = ({
  disabled,
  formik,
  id,
  inputHtml,
  label,
  labelAfter,
  labelHtml,
  name,
  tooltip,
  onBlur,
  onChange,
  placeholder,
  validate: { required, maxLength, minLength },
  withoutCSS,
  wrapperHtml,
  shouldReceiveFocus,
  showByDefault,
  nbrStars,
  ctaLabel,
  inputPWDType,
}) => {
  const simpleFormGemClass = 'string';
  const htmlId = id || name;
  const error = getIn(formik.errors, name);
  const inputRef = useRef(null);

  const [showPWD, setShowPWD] = useState(showByDefault);

  useEffect(() => {
    if (inputRef && shouldReceiveFocus) {
      inputRef.current.focus();
    }
  }, []);

  const labelTag = (
    <Label
      label={label}
      id={htmlId}
      required={required}
      withoutCSS={withoutCSS}
      simpleFormGemClass={simpleFormGemClass}
      {...labelHtml}
    />
  );

  const handleChange = useCallback(
    (e) => {
      formik.handleChange(e);
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [formik, onChange],
  );
  const handleBlur = useCallback(
    (e) => {
      formik.handleBlur(e);
      if (onBlur) {
        onBlur();
      }
    },
    [formik, onBlur],
  );

  inputHtml = addClassName({
    htmlProps: inputHtml,
    classes: 'uk-input string',
    shouldAdd: !withoutCSS,
  });

  const renderPlaceholder = useMemo(() => {
    if (showPWD) {
      return placeholder;
    }

    let stars = '';

    for (let i = 0; i < nbrStars; i++) {
      stars += '*';
    }

    return stars;
  }, [showPWD, placeholder]);

  const renderTogglePasswordButton = useMemo(() => {
    if (showPWD) {
      return null;
    }

    return (
      <Button
        ml={4}
        aria-label={ctaLabel || __('Modify')}
        onClick={() => {
          setShowPWD((previousShow) => !previousShow);
          // Put the focus event at the end of the JS pile
          setTimeout(() => {
            inputRef?.current?.focus();
          }, 0);
        }}
      >
        {ctaLabel || __('Modify')}
      </Button>
    );
  }, [showPWD, setShowPWD, inputRef, ctaLabel]);

  return (
    <Wrapper
      name={name}
      withoutCSS={withoutCSS}
      simpleFormGemClass={simpleFormGemClass}
      tooltip={tooltip}
      {...wrapperHtml}
    >
      {!labelAfter && labelTag}
      <PasswordContent>
        <CustomField
          disabled={!showPWD || disabled}
          type={showPWD ? 'text' : inputPWDType}
          name={name}
          placeholder={renderPlaceholder}
          id={htmlId}
          validate={(value) =>
            validatePassword({
              value,
              required,
              maxLength,
              minLength,
            })
          }
          onChange={handleChange}
          onBlur={handleBlur}
          data-testid="formik-password"
          innerRef={(input) => {
            inputRef.current = input;
          }}
          aria-invalid={error ? 'true' : null}
          aria-label={label}
          aria-required={required}
          {...inputHtml}
        />
        {renderTogglePasswordButton}
      </PasswordContent>
      {labelAfter && labelTag}
      <Error name={name} id={id} />
    </Wrapper>
  );
};

Password.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inputHtml: PropTypes.object,
  label: PropTypes.string,
  labelAfter: PropTypes.bool,
  labelHtml: PropTypes.object,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  tooltip: PropTypes.string,
  validate: PropTypes.shape({
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    minLength: PropTypes.number,
  }),
  withoutCSS: PropTypes.bool,
  wrapperHtml: PropTypes.object,
  shouldReceiveFocus: PropTypes.bool,
  showByDefault: PropTypes.bool,
  nbrStars: PropTypes.number,
  ctaLabel: PropTypes.node,
  inputPWDType: PropTypes.string,
};

Password.defaultProps = {
  inputHtml: {},
  labelAfter: false,
  labelHtml: {},
  validate: {},
  withoutCSS: false,
  wrapperHtml: {},
  shouldReceiveFocus: false,
  showByDefault: false,
  nbrStars: 6,
  inputPWDType: 'password',
};

export default connect(Password);

const CustomField = styled(Field)`
  &::placeholder {
    color: ${(props) => props.theme.colors.dove} !important;
  }
`;

const PasswordContent = styled(Box)`
  button {
    min-width: 100px;
  }
`;
